import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { CaptureError, ScreenWake, startCapture, type Capture } from "@/lib/capture.ts";
import { EngineConnection } from "@/lib/connection.ts";
import type { Prefs } from "@/lib/prefs.ts";
import type { Metrics, ReadyEvent, ServerEvent } from "@/lib/protocol.ts";
import { applyEvent, emptyTranscript, type Transcript } from "@/lib/transcript.ts";
import { wsUrl } from "@/lib/urls.ts";

export type Phase =
  | "idle"
  | "requesting-mic"
  | "connecting"
  | "live"
  | "reconnecting"
  | "finalizing"
  | "stopped"
  | "error";

export type Mode = "live" | "simulated";

type Action = { kind: "event"; event: ServerEvent } | { kind: "reset" };

function reducer(state: Transcript, action: Action): Transcript {
  return action.kind === "reset" ? emptyTranscript() : applyEvent(state, action.event);
}

export const BUSY: ReadonlySet<Phase> = new Set(["requesting-mic", "connecting", "live", "reconnecting", "finalizing"]);

export function useBooth(prefs: Prefs, accessCode: string) {
  const [transcript, dispatch] = useReducer(reducer, undefined, emptyTranscript);
  const [phase, setPhase] = useState<Phase>("idle");
  const [session, setSession] = useState<ReadyEvent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [captureInfo, setCaptureInfo] = useState<{ path: string; deviceRate: number; label: string } | null>(null);

  const level = useRef({ db: -120, peak: 0 });
  const conn = useRef<EngineConnection | null>(null);
  const capture = useRef<Capture | null>(null);
  const wake = useRef(new ScreenWake());
  const run = useRef(0);
  const phaseRef = useRef<Phase>("idle");
  const noticeTimer = useRef<number | undefined>(undefined);
  const stopTimer = useRef<number | undefined>(undefined);

  const go = useCallback((next: Phase) => {
    phaseRef.current = next;
    setPhase(next);
  }, []);

  const flash = useCallback((message: string | null, ms = 6000) => {
    window.clearTimeout(noticeTimer.current);
    setNotice(message);
    if (message) noticeTimer.current = window.setTimeout(() => setNotice(null), ms);
  }, []);

  const release = useCallback(async () => {
    const cap = capture.current;
    capture.current = null;
    level.current = { db: -120, peak: 0 };
    await cap?.stop();
    await wake.current.disable();
  }, []);

  const finish = useCallback(
    (next: "stopped" | "error", message?: string) => {
      window.clearTimeout(stopTimer.current);
      if (message) setError(message);
      go(next);
      void release();
    },
    [go, release],
  );

  const start = useCallback(
    async (mode: Mode, codeOverride?: string) => {
      if (BUSY.has(phaseRef.current)) return;
      const code = codeOverride ?? accessCode;
      const token = ++run.current;
      conn.current?.close();
      dispatch({ kind: "reset" });
      setSession(null);
      setError(null);
      setErrorCode(null);
      setMetrics(null);
      flash(null);

      if (mode === "live") {
        go("requesting-mic");
        try {
          const cap = await startCapture({
            deviceId: prefs.deviceId || undefined,
            processing: {
              echoCancellation: prefs.echoCancellation,
              noiseSuppression: prefs.noiseSuppression,
              autoGainControl: prefs.autoGainControl,
            },
            workletUrl: "/capture-worklet.js",
            onFrame: (pcm) => conn.current?.sendAudio(pcm),
            onLevel: (db, peak) => {
              level.current = { db, peak };
            },
            onEnded: (reason) => {
              flash(reason, 10000);
              conn.current?.stop();
              go("finalizing");
            },
          });
          if (token !== run.current) {
            await cap.stop();
            return;
          }
          capture.current = cap;
          setCaptureInfo({ path: cap.path, deviceRate: cap.deviceRate, label: cap.label });
        } catch (err) {
          setError(err instanceof CaptureError ? err.message : "The microphone could not be started.");
          go("error");
          return;
        }
      } else {
        setCaptureInfo(null);
      }

      go("connecting");
      const onEvent = (event: ServerEvent) => {
        if (token !== run.current) return;
        dispatch({ kind: "event", event });
        switch (event.type) {
          case "ready":
            setSession(event);
            if (phaseRef.current !== "finalizing") go("live");
            if (event.resumed) flash("Reconnected. The session continued where it left off.", 4000);
            break;
          case "status":
            if (event.state === "finalizing") go("finalizing");
            else if (event.state === "stopped") finish("stopped");
            break;
          case "error":
            if (event.fatal) {
              setErrorCode(event.code);
              finish("error", event.message);
              conn.current?.close();
            } else {
              flash(event.message);
            }
            break;
          case "metrics":
            setMetrics(event);
            break;
          default:
            break;
        }
      };
      const connection = new EngineConnection({
        url: wsUrl(),
        first: () => ({
          type: "start",
          protocol: 2,
          mode,
          direction: prefs.direction,
          source: prefs.source,
          target: prefs.target,
          sampleRate: 16000,
          channels: 1,
          encoding: "pcm_s16le",
          ...(mode === "live" && code ? { accessCode: code } : {}),
          listenerDelayS: prefs.listenerDelayS,
          ...(prefs.direction === "one-way" && prefs.secondTarget && ![prefs.source, prefs.target].includes(prefs.secondTarget)
            ? { extraTarget: prefs.secondTarget }
            : {}),
        }),
        resume: (info) => ({ type: "resume", ...info }),
        onEvent,
        onState: (state, detail) => {
          if (token !== run.current) return;
          if (state === "reconnecting" && phaseRef.current !== "connecting") {
            if (phaseRef.current !== "finalizing") go("reconnecting");
            flash("Connection lost. Reconnecting; your speech is buffered meanwhile.", 15000);
          } else if (state === "closed" && BUSY.has(phaseRef.current)) {
            const reason = detail.reason ? ` (${detail.reason})` : "";
            finish(
              phaseRef.current === "finalizing" ? "stopped" : "error",
              phaseRef.current === "finalizing" ? undefined : `The engine connection closed${reason}. Text so far is kept.`,
            );
          }
        },
      });
      conn.current = connection;
      connection.open();
      void wake.current.enable();
    },
    [accessCode, finish, flash, go, prefs],
  );

  const stop = useCallback(async () => {
    const connection = conn.current;
    if (!connection || !BUSY.has(phaseRef.current) || phaseRef.current === "finalizing") return;
    go("finalizing");
    await release();
    connection.stop();
    window.clearTimeout(stopTimer.current);
    stopTimer.current = window.setTimeout(() => {
      if (phaseRef.current === "finalizing") {
        connection.close();
        finish("stopped");
        flash("The engine did not confirm in time; the last lines may be incomplete.", 10000);
      }
    }, 45000);
  }, [finish, flash, go, release]);

  /** Change the listeners' audio buffer during a session; phones apply it at once. */
  const setListenerDelay = useCallback((seconds: number) => {
    if (BUSY.has(phaseRef.current)) conn.current?.send({ type: "settings", listenerDelayS: seconds });
  }, []);

  const clear = useCallback(() => {
    if (BUSY.has(phaseRef.current)) return;
    run.current += 1;
    conn.current?.close();
    conn.current = null;
    dispatch({ kind: "reset" });
    setSession(null);
    setMetrics(null);
    setError(null);
    setErrorCode(null);
    flash(null);
    go("idle");
  }, [flash, go]);

  useEffect(() => {
    const onHide = (event: PageTransitionEvent) => {
      // Leaving the page (not just backgrounding it): finalize what was said.
      if (!event.persisted && BUSY.has(phaseRef.current)) conn.current?.stop();
    };
    window.addEventListener("pagehide", onHide);
    return () => {
      window.removeEventListener("pagehide", onHide);
      run.current += 1;
      conn.current?.close();
      void release();
    };
  }, [release]);

  return {
    transcript,
    phase,
    busy: BUSY.has(phase),
    session,
    error,
    errorCode,
    notice,
    metrics,
    captureInfo,
    level,
    start,
    stop,
    clear,
    setListenerDelay,
  };
}
